// Phase I (I-B, I2): types and hooks for the tenant's subscription and entitlements, matching
// /api/subscription. The API enforces plans; the web only hides what is off.
import { useQuery } from '@tanstack/react-query';
import type { Entitlements, PlanAddon, PlanFeature, PlanLimit, PlanModule, SubscriptionPreview, SubscriptionStatus } from '@denwen-msp/shared';
import { useAuth } from '@/app/auth';
import { api } from '@/lib/api';

export const ENTITLEMENTS_KEY = ['subscription', 'entitlements'];

/** What this tenant may use. Undefined while loading (show everything until we know). */
export function useEntitlements() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ENTITLEMENTS_KEY,
    queryFn: ({ signal }) => api<Entitlements>('/subscription/entitlements', { signal }),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
}

/** True unless the plan is known to leave the module out. */
export function useModuleEnabled() {
  const ent = useEntitlements();
  return (m: PlanModule | undefined) => !m || !Array.isArray(ent.data?.modules) || ent.data.modules.includes(m);
}

/** URL prefixes of the web app that belong to a plan module. */
export const MODULE_PATHS: Record<PlanModule, string[]> = {
  hr: ['/hr', '/me'],
  payroll: ['/payroll'],
  monitoring: ['/monitoring'],
  accounting: ['/accounting', '/payables', '/banking'],
};

export function moduleForPath(pathname: string): PlanModule | null {
  for (const [m, prefixes] of Object.entries(MODULE_PATHS) as [PlanModule, string[]][]) {
    if (prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return m;
  }
  return null;
}

export interface PlanOption {
  key: string;
  name: string;
  description: string | null;
  seatPriceMinor: number | null;
  currency: string;
  interval: 'month' | 'year';
  modules: PlanModule[];
  features: PlanFeature[];
  limits: Partial<Record<PlanLimit, number>>;
  addons: Omit<PlanAddon, 'stripePriceId'>[];
  trialDays: number;
  purchasable: boolean;
}

export interface LastInvoice {
  id: string;
  status: string | null;
  amountDue: number | null;
  amountPaid: number | null;
  currency: string | null;
  hostedUrl: string | null;
  at: number;
}

export interface SubscriptionOverview {
  entitlements: Entitlements;
  subscription: {
    status: SubscriptionStatus;
    seats: number;
    /** Seats the subscription drops to at the next renewal. */
    pendingSeats: number | null;
    addons: string[];
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    trialEndsAt: string | null;
    hasPaymentMethod: boolean;
    pastDueSince: string | null;
    lastInvoice: LastInvoice | null;
    hasStripeSubscription: boolean;
    hasCustomer: boolean;
  } | null;
  plans: PlanOption[];
  billing: { configured: boolean; mode: 'test' | 'live' };
}

export type { SubscriptionPreview };

/** Minor units → "£12.34". */
export function money(minor: number | null | undefined, currency = 'GBP') {
  if (minor === null || minor === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(minor / 100);
}
